import { defaultProviderId, getProvider, isProviderId, type ProviderId } from '../providers';
import { GAME_MAP_BY_ID, type GameMapId } from './maps';
import { readJson, writeJson } from './storage';

export const TIME_LIMITS = [0, 180, 120, 60, 30] as const;
export type TimeLimit = (typeof TIME_LIMITS)[number];

export interface GameSettings {
  providerId: ProviderId;
  mapId: GameMapId;
  /** Seconds per round, 0 = unlimited. */
  timeLimit: TimeLimit;
  /** Whether the player may move along the road. */
  allowMove: boolean;
}

const KEY = 'geogessr:settings:v1';

export function defaultSettings(): GameSettings {
  return { providerId: defaultProviderId(), mapId: 'world', timeLimit: 0, allowMove: true };
}

/** Returns valid settings, repairing anything stale (provider no longer configured, unsupported map…). */
export function sanitizeSettings(input: Partial<GameSettings> | null | undefined): GameSettings {
  const defaults = defaultSettings();
  const s = { ...defaults, ...(input ?? {}) };
  if (!isProviderId(s.providerId) || !getProvider(s.providerId).isConfigured()) s.providerId = defaults.providerId;
  if (!(s.mapId in GAME_MAP_BY_ID)) s.mapId = defaults.mapId;
  if (!getProvider(s.providerId).supportedMaps.includes(s.mapId)) s.mapId = 'world';
  if (!(TIME_LIMITS as readonly number[]).includes(s.timeLimit)) s.timeLimit = 0;
  s.allowMove = s.allowMove !== false;
  return s;
}

export function loadSettings(): GameSettings {
  return sanitizeSettings(readJson<Partial<GameSettings>>(KEY));
}

export function saveSettings(settings: GameSettings): void {
  writeJson(KEY, settings);
}

export function formatTimeLimit(seconds: number): string {
  if (seconds === 0) return 'Illimité';
  if (seconds < 60) return `${seconds} s`;
  return `${seconds / 60} min`;
}
